import { useEffect, useState } from "react";
import { Trans, useTranslation } from "react-i18next";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { getConsent, setConsent } from "@/lib/consent";
import { Link } from "react-router-dom";

// EU/EEA + UK — regions where GDPR (or equivalent) requires a consent prompt.
const GDPR_COUNTRIES = new Set([
  "AT","BE","BG","HR","CY","CZ","DK","EE","FI","FR","DE","GR","HU","IE","IT",
  "LV","LT","LU","MT","NL","PL","PT","RO","SK","SI","ES","SE",
  "IS","LI","NO", // EEA
  "GB","CH", // UK + Switzerland (FADP, similar regime)
]);

const isGdprRegion = async (): Promise<boolean> => {
  try {
    const res = await fetch("https://ipapi.co/country/", { cache: "force-cache" });
    if (!res.ok) return false;
    const country = (await res.text()).trim().toUpperCase();
    return GDPR_COUNTRIES.has(country);
  } catch {
    return false;
  }
};

/**
 * Analytics is on by default. On first visit, show a small non-blocking
 * corner toast letting visitors decline. A "Cookie settings" link in the
 * footer re-opens a dialog so the choice can be changed at any time.
 */
export const CookieConsent = () => {
  const { t } = useTranslation();
  const [dialogOpen, setDialogOpen] = useState(false);
  const [currentChoice, setCurrentChoice] = useState(() => getConsent());

  useEffect(() => {
    if (getConsent() !== null) return;

    let timeoutId: number | undefined;
    let cancelled = false;

    isGdprRegion().then((required) => {
      if (cancelled || !required) return;
      timeoutId = window.setTimeout(() => {
        const toastId = toast.custom(
          () => (
            <div className="flex flex-col gap-2 w-[300px] rounded-lg border bg-background p-3 shadow-lg">
              <p className="text-xs font-semibold leading-none">
                {t("cookieConsent.toast.title")}
              </p>
              <p className="text-xs leading-snug text-muted-foreground">
                <Trans
                  i18nKey="cookieConsent.toast.body"
                  components={{
                    a: <a href="/legal/cookie-policy" className="underline" />,
                  }}
                />
              </p>
              <div className="flex flex-col items-stretch gap-1 mt-0.5">
                <Button
                  size="sm"
                  className="w-full h-8 text-xs"
                  onClick={() => {
                    setConsent("accepted");
                    setCurrentChoice("accepted");
                    toast.dismiss(toastId);
                  }}
                >
                  {t("cookieConsent.toast.ok")}
                </Button>
                <button
                  type="button"
                  onClick={() => {
                    setConsent("declined");
                    setCurrentChoice("declined");
                    toast.dismiss(toastId);
                  }}
                  className="self-center text-[11px] text-muted-foreground/70 hover:text-muted-foreground underline underline-offset-2 transition-colors"
                >
                  {t("cookieConsent.toast.decline")}
                </button>
              </div>
            </div>
          ),
          { duration: Infinity, unstyled: true, classNames: { toast: "!bg-transparent !shadow-none !p-0 !border-0" } }
        );
      }, 1500);
    });

    return () => {
      cancelled = true;
      if (timeoutId) window.clearTimeout(timeoutId);
    };
  }, [t]);

  useEffect(() => {
    const open = () => {
      setCurrentChoice(getConsent());
      setDialogOpen(true);
    };
    window.addEventListener("cookieconsent:open", open);
    return () => window.removeEventListener("cookieconsent:open", open);
  }, []);

  const apply = (value: "accepted" | "declined") => {
    setConsent(value);
    setCurrentChoice(value);
    setDialogOpen(false);
    toast.success(t("cookieConsent.saved"));
  };

  return (
    <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{t("cookieConsent.dialog.title")}</DialogTitle>
          <DialogDescription>
            {t("cookieConsent.dialog.body")}{" "}
            <Link to="/legal/cookie-policy" className="underline">
              {t("cookieConsent.dialog.policyLink")}
            </Link>
            .
          </DialogDescription>
        </DialogHeader>
        {currentChoice && (
          <p className="text-xs text-muted-foreground">
            {t("cookieConsent.dialog.current", {
              status:
                currentChoice === "accepted"
                  ? t("cookieConsent.status.accepted")
                  : t("cookieConsent.status.declined"),
            })}
          </p>
        )}
        <DialogFooter className="gap-2 sm:gap-2">
          <Button variant="outline" onClick={() => apply("declined")}>
            {t("cookieConsent.dialog.decline")}
          </Button>
          <Button onClick={() => apply("accepted")}>
            {t("cookieConsent.dialog.accept")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default CookieConsent;